import { db } from "../firebase";
import { doc, setDoc, arrayUnion, query, where, getDocs, collectionGroup, updateDoc, getDoc } from "firebase/firestore";
import { WORKFLOW_WERKER_TYPES, PUSH_NOTIFICATION_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { fx_FB_SendNotification } from "../functions/Fx_SendPushNotification";
import { fx_DocStamp } from "../functions/Fx_DocStamp";
import { SUPPORT_MONKEY_ASSOCIATE, SUPPORT_MONKEY_UID, ADMIN_UIDS_FIELD, ADMIN_CACHE_DURATION_MS } from "../constants/supportConstants";
import { chatFunction } from "../functions/Fx_Chats";
import { fx_FB_JobChatMessagePending } from "../functions/fx_FB_JobChatMessagePending";

// Admin UIDs cache to reduce Firestore reads
let adminUidsCache: { uids: string[], timestamp: number } | null = null;

/**
 * Fetches admin UIDs from Firestore with caching.
 * Returns empty array if document doesn't exist or field is missing.
 */
const getAdminUids = async (): Promise<string[]> => {
  try {
    // Check cache
    if (adminUidsCache && (Date.now() - adminUidsCache.timestamp) < ADMIN_CACHE_DURATION_MS) {
      return adminUidsCache.uids;
    }

    // Fetch from Firestore
    const featureFlagsRef = doc(db, 'userSettings', '00FeatureFlagSettings');
    const featureFlagsSnap = await getDoc(featureFlagsRef);

    if (!featureFlagsSnap.exists()) {
      console.warn('Admin UIDs: userSettings/00FeatureFlagSettings document not found');
      return [];
    }

    const data = featureFlagsSnap.data();
    const adminUids = data?.[ADMIN_UIDS_FIELD];

    if (!Array.isArray(adminUids)) {
      console.warn(`Admin UIDs: ${ADMIN_UIDS_FIELD} field is missing or not an array`);
      return [];
    }

    // Filter out empty/invalid UIDs
    const validUids = adminUids.filter(uid => typeof uid === 'string' && uid.trim());

    // Update cache
    adminUidsCache = {
      uids: validUids,
      timestamp: Date.now()
    };

    return validUids;
  } catch (error) {
    console.error('Error fetching admin UIDs:', error);
    return [];
  }
};

export interface UpdateChatDocParams {
  chatDocOwner: string;
  newMsgDate: number;
  picURL: string;
  msgPreview: string;
  msgIsRead: boolean;
  msgTitle: string;
  dmUID: string;
  chatId: string;
}

export interface ChatDocData {
  messageDate: number;
  sendersPic: string;
  messagePreview: string;
  isRead: boolean;
  messageTitle: string;
  DMuid: string;
}

export interface DMParticipant {
  uid: string;
  picURL: string;
  name: string;
}

export interface JobChatContext {
  jobName: string;
  schedulersPic: string;
  isScheduler: boolean;
}

export interface ProcessDMParams {
  chatId: string;
  message: string;
  profile: any;
  myChats: any[];
  myAssociates: any[];
}

export interface ProcessJobMessageParams {
  chatId: string;
  message: string;
  profile: any;
  isScheduler: boolean;
  schedJob: any[];
  werkerJob: any[];
  clientId?: string;
}

export interface ProcessDMParamsWithAssociate {
  chatId: string;
  message: string;
  profile: any;
  myChats: any[];
  myAssociates: any[];
  associate?: any; // Optional associate object for first-time chats
  clientId?: string;
}

export interface SendSupportMonkeyMessageParams {
  chatId: string;
  message: string;
  chatMeta: any;
  clientId?: string;
}

export interface ChatMessageV3 {
  schemaVersion: 1;
  clientId: string;
  chatId: string;

  text: string;

  fromUid: string;
  fromName: string;
  fromPic: string;

  createdAt: number;
  updatedAt: number;
  
  // Optional metadata for job support requests
  jobSupportMeta?: {
    uniqueJobId: string;
    schedUid: string;
    viewType: "werker" | "scheduler";
    docId: string;
    startDate: string;
    location: string;
    seriesIndex?: number | null;
    seriesTotal?: number | null;
    werkerUid?: string; // Only present when viewType is "werker"
  };
}

const _resolveJobNameFromParticipants = (chatId: string, participants: any[]): string => {
  // Try common paths (be defensive — your schema has evolved)
  const p0 = participants?.[0] || {};

  const candidates = [
    p0?.jobInfo?.core?.shiftIdentifier,
    p0?.jobInfo?.core?.ShiftIdentifier,
    p0?.jobInfo?.core?.shiftId,
    p0?.jobInfo?.core?.jobName,
    p0?.jobInfo?.context?.shiftIdentifier,
    p0?.jobInfo?.context?.ShiftIdentifier,
    p0?.jobInfo?.details?.shiftIdentifier,
    p0?.jobInfo?.details?.ShiftIdentifier,
    p0?.jobInfo?.core?.company, // last-resort-ish, but better than blank
  ];

  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s) return s;
  }

  // Final fallback: never blank
  const short = String(chatId || "").slice(-6);
  return short ? `Job ${short}` : "Job Chat";
};

const ensureMsgId = (clientId: string | undefined, profile: any, now: number) => {
  const trimmed = (clientId || "").trim();
  if (trimmed) return trimmed;
  return `${profile.UserId}_${now}_${Math.random().toString(16).slice(2)}`;
};

// Process a direct message (DM) between users ///
export const processDMMessage = async (params: ProcessDMParamsWithAssociate): Promise<void> => {
  const { chatId, message, profile, myChats, myAssociates, associate, clientId } = params;
  const trimmedMessage = trimMessagePreview(message);
  const newMsgDate = Date.now();

  let participant;
  // Try to find participant in existing chats first
  participant = findDMParticipant(chatId, myChats, myAssociates);
  // If not found and associate is provided, use the associate data directly
  if (!participant && associate) {
    participant = {
      uid: associate.WerkerUid,
      picURL: associate.WerkerProfilePicURL,
      name: `${associate.WerkerFirstName} ${associate.WerkerLastName}`,
    };
  }
  if (!participant) {
    throw new Error("Could not find DM participant and no associate provided");
  }
  const baseParams = {
    newMsgDate,
    msgPreview: trimmedMessage,
    chatId,
  };
  // Update sender's chat doc
  await updateChatDocument({
    ...baseParams,
    chatDocOwner: profile.UserId,
    picURL: participant.picURL,
    msgIsRead: true,
    msgTitle: participant.name,
    dmUID: participant.uid,
  });
  // Update receiver's chat doc
  await updateChatDocument({
    ...baseParams,
    chatDocOwner: participant.uid,
    picURL: profile.ProfilePicURL,
    msgIsRead: false,
    msgTitle: `${profile.FirstName} ${profile.LastName}`,
    dmUID: profile.UserId,
  });
  // Store the actual message in chat document
  const docStamp = fx_DocStamp(
    Date.now(),
    "NA",
    "NA",
    "NA",
    "NA",
    profile.UserId,
    profile.FirstName,
    profile.LastName,
    profile.ProfilePicURL,
    "TBD",
    "TBD",
    "/chat",
    "NA",
    "NA",
    "TBD"
  );

  const chatDocRef = doc(db, FB_COLLECTION_PATH.Chat, chatId);
  await setDoc(
    chatDocRef,
    {
      chatCategory: "DM",
      messages: arrayUnion({
        docStamp,
        chatContent: {
          text: message,
          chatPicURL: "",
          chatID: chatId,
          ...(clientId ? { clientId } : {}),
        },
      }),
    },
    { merge: true }
  );

  // ✅ Release A: Dual-write to v3 subcollection (idempotent docId = clientId)
  const msgId = ensureMsgId(clientId, profile, newMsgDate);

  const v3: ChatMessageV3 = {
    schemaVersion: 1,
    clientId: msgId,
    chatId,

    text: message,

    fromUid: profile.UserId,
    fromName: `${profile.FirstName} ${profile.LastName}`.trim(),
    fromPic: profile.ProfilePicURL || "",

    createdAt: newMsgDate,
    updatedAt: newMsgDate,
  };

  const msgRef = doc(db, FB_COLLECTION_PATH.Chats, chatId, "messages", msgId);
  await setDoc(msgRef, v3, { merge: true });

  // If sending to the Support Monkey, send email notification to support team
  // if (participant.uid === SUPPORT_MONKEY_UID) {
  //   await sendSupportEmailNotification({
  //     senderProfile: {
  //       UserId: profile.UserId,
  //       FirstName: profile.FirstName,
  //       LastName: profile.LastName,
  //       Email: profile.Email || profile.email, // Adjust based on your profile structure
  //     },
  //     message: message,
  //     chatId: chatId,
  //     timestamp: newMsgDate
  //   });
  // }

  // Send push notification
  await fx_FB_SendNotification(profile.UserId, [participant.uid], PUSH_NOTIFICATION_TYPES.DirectMessage, {
    senderName: `${profile.FirstName} ${profile.LastName}`,
    msgPreview: trimmedMessage,
    deepLink: "",
    senderUID: profile.UserId,
    chatId: chatId,
  });

  // If messaging support monkey, also notify admins
  if (participant.uid === SUPPORT_MONKEY_UID) {
    try {
      const adminUids = await getAdminUids();
      if (adminUids.length > 0) {
        await fx_FB_SendNotification(
          profile.UserId,
          adminUids,
          PUSH_NOTIFICATION_TYPES.DirectMessage,
          {
            senderName: `MONKEY: ${profile.FirstName} ${profile.LastName}`,
            msgPreview: trimmedMessage,
            deepLink: "",
          }
        );
      }
    } catch (error) {
      console.error('Error sending admin notifications for support DM:', error);
    }
  }
};

export const sendSupportMonkeyMessage = async (
  params: SendSupportMonkeyMessageParams
): Promise<void> => {
  const { chatId, message, chatMeta, clientId } = params;
  const trimmedMessage = trimMessagePreview(message);
  const newMsgDate = Date.now();

  const recipientUid = String(chatMeta?.DMuid || "").trim();
  if (!recipientUid) {
    throw new Error("Support monkey chat is missing DM recipient uid");
  }

  const supportName = `${SUPPORT_MONKEY_ASSOCIATE.WerkerFirstName} ${SUPPORT_MONKEY_ASSOCIATE.WerkerLastName}`.trim();

  await updateChatDocument({
    chatDocOwner: SUPPORT_MONKEY_UID,
    newMsgDate,
    picURL: String(chatMeta?.SendersPic || ""),
    msgPreview: trimmedMessage,
    msgIsRead: true,
    msgTitle: String(chatMeta?.MessageTitle || "Support Chat"),
    dmUID: recipientUid,
    chatId,
  });

  await updateChatDocument({
    chatDocOwner: recipientUid,
    newMsgDate,
    picURL: SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL,
    msgPreview: trimmedMessage,
    msgIsRead: false,
    msgTitle: supportName,
    dmUID: SUPPORT_MONKEY_UID,
    chatId,
  });

  const docStamp = fx_DocStamp(
    newMsgDate,
    "NA",
    "NA",
    "NA",
    "NA",
    SUPPORT_MONKEY_UID,
    SUPPORT_MONKEY_ASSOCIATE.WerkerFirstName,
    SUPPORT_MONKEY_ASSOCIATE.WerkerLastName,
    SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL,
    "TBD",
    "TBD",
    "/chat",
    "NA",
    "NA",
    "TBD"
  );

  const chatDocRef = doc(db, FB_COLLECTION_PATH.Chat, chatId);
  await setDoc(
    chatDocRef,
    {
      chatCategory: "DM",
      messages: arrayUnion({
        docStamp,
        chatContent: {
          text: message,
          chatPicURL: "",
          chatID: chatId,
          ...(clientId ? { clientId } : {}),
        },
      }),
    },
    { merge: true }
  );

  const msgId = ensureMsgId(clientId, { UserId: SUPPORT_MONKEY_UID }, newMsgDate);

  const v3: ChatMessageV3 = {
    schemaVersion: 1,
    clientId: msgId,
    chatId,
    text: message,
    fromUid: SUPPORT_MONKEY_UID,
    fromName: supportName,
    fromPic: SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL,
    createdAt: newMsgDate,
    updatedAt: newMsgDate,
  };

  const msgRef = doc(db, FB_COLLECTION_PATH.Chats, chatId, "messages", msgId);
  await setDoc(msgRef, v3, { merge: true });

  await fx_FB_SendNotification(SUPPORT_MONKEY_UID, [recipientUid], PUSH_NOTIFICATION_TYPES.DirectMessage, {
    senderName: supportName,
    msgPreview: trimmedMessage,
    deepLink: "",
    senderUID: SUPPORT_MONKEY_UID,
  });
};

// Process a job-related message ////////////////////////////
export const processJobMessage = async (params: ProcessJobMessageParams): Promise<void> => {
  const { chatId, message, profile, isScheduler, schedJob, werkerJob, clientId } = params;
  const newMsgDate = Date.now();
  const msgId = ensureMsgId(clientId, profile, newMsgDate);

  const jobContext = getJobChatContext(chatId, isScheduler, schedJob, werkerJob);
  const jobName = String(jobContext?.jobName || "").trim();
  const chatPreviewPicURL = isScheduler
    ? profile?.ProfilePicURL || ""
    : String(jobContext?.schedulersPic || "").trim() || profile?.ProfilePicURL || "";

  await fx_FB_JobChatMessagePending({
    senderUID: profile.UserId,
    chatId,
    message,
    clientId: msgId,
    senderFirstName: profile.FirstName || "",
    senderLastName: profile.LastName || "",
    senderPicURL: profile.ProfilePicURL || "",
    chatPreviewPicURL,
    jobName,
    timestamp: newMsgDate,
  });
};

// Main function to send chat message ///
export const sendChatMessage = async (
  chatId: string,
  message: string,
  chatType: string,
  profile: any,
  myChats: any[],
  myAssociates: any[],
  schedJob: any[],
  werkerJob: any[],
  clientId?: string
): Promise<void> => {
  if (!message.trim()) return;
  try {
    if (chatType === "DM") {
      await processDMMessage({
        chatId,
        message,
        profile,
        myChats,
        myAssociates,
        clientId,
      });
    } else {
      await processJobMessage({
        chatId,
        message,
        profile,
        isScheduler: profile.UserLastRoleScheduler,
        schedJob,
        werkerJob,
        clientId,
      });
    }
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

// Helper functions ////////////////////////////
export const trimMessagePreview = (message: string): string => {
  if (message.length <= 25) {
    return message;
  }
  return message.slice(0, 22) + "...";
};
export const updateChatDocument = async (params: UpdateChatDocParams): Promise<void> => {
  const { chatDocOwner, newMsgDate, picURL, msgPreview, msgIsRead, msgTitle, dmUID, chatId } = params;
  try {
    const chatDocRef = doc(db, FB_COLLECTION_PATH.Chats, chatDocOwner);
    const chatData: ChatDocData = {
      messageDate: newMsgDate,
      sendersPic: picURL,
      messagePreview: msgPreview,
      isRead: msgIsRead,
      messageTitle: msgTitle,
      DMuid: dmUID,
    };
    await setDoc(
      chatDocRef,
      {
        [chatId]: chatData,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error updating chat document:", error);
    throw error;
  }
};

export const findDMParticipant = (chatId: string, myChats: any[], myAssociates: any[]): DMParticipant | null => {
  let receiverUID = "";

  // Find receiver UID from chats
  const chat = myChats.find((chat) => chat.Key === chatId);
  if (!chat) return null;

  receiverUID = chat.DMuid;

  // Find receiver details from associates
  const associate = myAssociates.find((assoc) => assoc.WerkerUid === receiverUID);
  if (!associate) return null;

  return {
    uid: receiverUID,
    picURL: associate.WerkerProfilePicURL,
    name: `${associate.WerkerFirstName} ${associate.WerkerLastName}`,
  };
};

export const getJobChatContext = (
  chatId: string,
  isScheduler: boolean,
  schedJobs: any[],
  werkerJobs: any[]
): JobChatContext => {
  let jobName = "";
  let schedulersPic = "";

  if (isScheduler) {
    const job = schedJobs.find((job) => job.JobDocId === chatId);
    if (job) {
      jobName = job.ShiftIdentifier;
    }
  } else {
    const job = werkerJobs.find((job) => job.JobDocId === chatId);
    if (job) {
      jobName = job.ShiftIdentifier;
      schedulersPic = job.SchedProfilePic;
    }
  }

  return {
    jobName,
    schedulersPic,
    isScheduler,
  };
};

export const getJobParticipants = async (chatId: string) => {
  const msgRecipientsQuery = query(
    collectionGroup(db, "jobsDocs"),
    where("jobInfo.core.uniqueJobId", "==", chatId),
    where("jobInfo.status.jobStatusWerker", "not-in", [
      WORKFLOW_WERKER_TYPES.Expired,
      WORKFLOW_WERKER_TYPES.Fired,
      WORKFLOW_WERKER_TYPES.Uninvited,
    ])
  );

  const querySnapshot = await getDocs(msgRecipientsQuery);
  return querySnapshot.docs.map((doc) => doc.data());
};

export const updateJobDocNewMessage = async (chatId: string, recipientUid: string) => {
  const jobDocQuery = query(
    collectionGroup(db, "jobsDocs"),
    where("jobInfo.core.uniqueJobId", "==", chatId),
    where("docStamp.userInfoTo.uid", "==", recipientUid)
  );

  try {
    const querySnapshot = await getDocs(jobDocQuery);
    querySnapshot.forEach(async (docSnap) => {
      const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, docSnap.id);
      await updateDoc(jobDocRef, {
        "jobInfo.status.isNewMessage": true,
      });
    });
  } catch (error) {
    console.error("Error updating job doc new message flag:", error);
  }

  try {
    const docRef = doc(db, FB_COLLECTION_PATH.Users, recipientUid, FB_COLLECTION_PATH.JobFeed, chatId);
    // Upsert to avoid noisy errors when the recipient's feed doc is created slightly later.
    await setDoc(docRef, {
      "isNewMessage": true,
    }, { merge: true });
  } catch (error2) {
    console.error("Error updating job feed new message flag:", error2);
  }


};

export interface SendJobToSupportParams {
  profile: any;
  uniqueJobId: string;
  schedUid: string;
  shiftIdentifier: string;
  viewType: "werker" | "scheduler";
  userNote: string;
  startDate: string;
  location: string;
  docId: string;
  seriesIndex?: number | null;
  seriesTotal?: number | null;
}

export const sendJobToSupport = async (params: SendJobToSupportParams): Promise<void> => {
  const { profile, uniqueJobId, schedUid, shiftIdentifier, viewType, userNote, startDate, location, docId, seriesIndex, seriesTotal } = params;

  const chatId = await chatFunction(profile, SUPPORT_MONKEY_ASSOCIATE, "DM");
  
  const trimmedMessage = trimMessagePreview(userNote || "Job support request");
  const newMsgDate = Date.now();

  // Format series info if applicable
  const seriesInfo = seriesIndex && seriesTotal ? ` (${seriesIndex}/${seriesTotal})` : "";
  
  // Build user-facing message body (visible to sender)
  let userMessage = `📋 Job Support: ${shiftIdentifier}${seriesInfo}`;
  if (userNote.trim()) {
    userMessage += `\n\n${userNote.trim()}`;
  }
  userMessage += `\n\n📅 Start: ${startDate}`;
  if (seriesInfo) {
    userMessage += `\n🔢 Series: ${seriesIndex}/${seriesTotal}`;
  }
  userMessage += `\n📍 Location: ${location}`;

  // Job metadata - stored separately, not in message text
  const jobMetadata: {
    uniqueJobId: string;
    schedUid: string;
    viewType: "werker" | "scheduler";
    docId: string;
    startDate: string;
    location: string;
    seriesIndex?: number | null;
    seriesTotal?: number | null;
    werkerUid?: string;
  } = {
    uniqueJobId,
    schedUid,
    viewType,
    docId,
    startDate,
    location,
    seriesIndex,
    seriesTotal,
  };

  // Only include werkerUid if viewType is "werker"
  if (viewType === "werker") {
    jobMetadata.werkerUid = profile.UserId;
  }

  // Update sender's chat doc
  await updateChatDocument({
    chatDocOwner: profile.UserId,
    newMsgDate,
    picURL: SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL,
    msgPreview: trimmedMessage,
    msgIsRead: true,
    msgTitle: `${SUPPORT_MONKEY_ASSOCIATE.WerkerFirstName} ${SUPPORT_MONKEY_ASSOCIATE.WerkerLastName}`,
    dmUID: SUPPORT_MONKEY_UID,
    chatId,
  });

  // Update Support Monkey's chat doc
  await updateChatDocument({
    chatDocOwner: SUPPORT_MONKEY_UID,
    newMsgDate,
    picURL: profile.ProfilePicURL,
    msgPreview: trimmedMessage,
    msgIsRead: false,
    msgTitle: `${profile.FirstName} ${profile.LastName}`,
    dmUID: profile.UserId,
    chatId,
  });

  // Store the full message (with admin details)
  const docStamp = fx_DocStamp(
    newMsgDate,
    "NA",
    "NA",
    "NA",
    "NA",
    profile.UserId,
    profile.FirstName,
    profile.LastName,
    profile.ProfilePicURL,
    "TBD",
    "TBD",
    "/chat",
    "NA",
    "NA",
    "TBD"
  );

  const chatDocRef = doc(db, FB_COLLECTION_PATH.Chat, chatId);
  const clientId = `${profile.UserId}_${newMsgDate}_${Math.random().toString(16).slice(2)}`;
  
  await setDoc(
    chatDocRef,
    {
      chatCategory: "DM",
      messages: arrayUnion({
        docStamp,
        chatContent: {
          text: userMessage,
          chatPicURL: "",
          chatID: chatId,
          clientId,
        },
      }),
    },
    { merge: true }
  );

  // Write v3 message with job metadata
  const msgId = clientId;
  const v3: ChatMessageV3 = {
    schemaVersion: 1,
    clientId: msgId,
    chatId,
    text: userMessage,
    fromUid: profile.UserId,
    fromName: `${profile.FirstName} ${profile.LastName}`.trim(),
    fromPic: profile.ProfilePicURL || "",
    createdAt: newMsgDate,
    updatedAt: newMsgDate,
    jobSupportMeta: jobMetadata,
  };

  const msgRef = doc(db, FB_COLLECTION_PATH.Chats, chatId, "messages", msgId);
  await setDoc(msgRef, v3, { merge: true });

  // Send push notification to Support Monkey
  await fx_FB_SendNotification(profile.UserId, [SUPPORT_MONKEY_UID], PUSH_NOTIFICATION_TYPES.DirectMessage, {
    senderName: `${profile.FirstName} ${profile.LastName}`,
    msgPreview: trimmedMessage,
    deepLink: "",
    senderUID: profile.UserId,
  });

  // Also notify admins with job details
  try {
    const adminUids = await getAdminUids();
    if (adminUids.length > 0) {
      // Format series info for notification
      const _seriesInfoText = seriesIndex && seriesTotal ? ` (${seriesIndex}/${seriesTotal})` : "";
      
      await fx_FB_SendNotification(
        profile.UserId,
        adminUids,
        PUSH_NOTIFICATION_TYPES.DirectMessage,
        {
          senderName: `MONKEY: ${profile.FirstName} ${profile.LastName}`,
          msgPreview: trimmedMessage,
          deepLink: "",
          // Include job identifiers for future deep linking
          jobSupportDetails: {
            docId,
            uniqueJobId,
            shiftIdentifier,
            startDate,
            location,
            viewType,
            seriesIndex,
            seriesTotal,
          },
        }
      );
    }
  } catch (error) {
    console.error('Error sending admin notifications for job support request:', error);
  }
};
